import { FC, useCallback } from 'react';
import { useMutation } from '@apollo/client';

import { FeatureName } from '__generated__/GQL';
import useCanCompanyUserAccessFeature from 'common/hooks/useCanCompanyUserAccessFeature';
import SunshineCard, { SunshineCardProps } from 'components/_core/SunshineCard';
import useToast from 'components/ToastProvider/useToast';

import { CreditCard } from '../../../../../../hooks/useCreditCards';
import locales from './locales';
import {
  UpdateCreditCardOptionsDocument,
  UpdateCreditCardOptionsMutation,
} from './updateCreditCardOptions.gql';

interface CardOptionsProps {
  creditCard: CreditCard;
}

const CardOptions: FC<CardOptionsProps> = ({ creditCard }) => {
  const { showToast } = useToast();
  const { canAccessFeature } = useCanCompanyUserAccessFeature();
  const { creditCardId, isPhysical, optionAtm, optionNfc, optionOnline } =
    creditCard;
  const canOrderPaymentCard = canAccessFeature({
    name: FeatureName.PaymentcardsOrder,
  });

  const [updateCreditCardOptions] = useMutation(
    UpdateCreditCardOptionsDocument,
    {
      optimisticResponse: ({ input }) =>
        ({
          __typename: 'Mutation',
          updateCreditCardOptions: {
            __typename: 'CreditCardPayload',
            creditCard: {
              ...creditCard,
              ...input,
            },
          },
        }) as UpdateCreditCardOptionsMutation,
    },
  );

  const onToggle = useCallback(
    (key: 'optionAtm' | 'optionNfc' | 'optionOnline') => async () => {
      try {
        const newOptionValue = Boolean(!creditCard[key]);
        await updateCreditCardOptions({
          variables: {
            input: { creditCardId, [key]: newOptionValue },
          },
        });
        showToast({
          message: newOptionValue
            ? locales.successToggleOn
            : locales.successToggleOff,
          type: 'success',
        });
      } catch (err) {
        showToast({ message: locales.errorMessage, type: 'error' });
      }
    },
    [creditCard, creditCardId, showToast, updateCreditCardOptions],
  );

  const commonOptions: SunshineCardProps[] = [
    {
      id: 'optionNfc',
      isChecked: optionNfc,
      onChange: onToggle('optionNfc'),
      title: locales.nfcOption,
      variant: 'switch',
    },
    {
      id: 'optionOnline',
      isChecked: optionOnline,
      isDisabled: !canOrderPaymentCard,
      onChange: onToggle('optionOnline'),
      title: locales.onlineOption,
      variant: 'switch',
    },
  ];

  const atmOption: SunshineCardProps = {
    id: 'optionAtm',
    isChecked: optionAtm,
    isDisabled: !canOrderPaymentCard,
    onChange: onToggle('optionAtm'),
    title: locales.atmOption,
    variant: 'switch',
  };

  const options = isPhysical ? [...commonOptions, atmOption] : commonOptions;

  return (
    <SunshineCard.Group isBoxed={false}>
      {options.map(({ id, ...option }) => (
        <SunshineCard alignItems="center" {...option} key={id} />
      ))}
    </SunshineCard.Group>
  );
};

export default CardOptions;
